import type { LeaderboardRankResponseItem } from '@paintscout/api'
import { removeAuth0Prefix } from '../../../users'
/**
 * Get the content for gamified callouts
 */

export interface DashboardEstimator {
  value: string
  label: string
  playerName?: string
  hasJoinedLeaderboard?: boolean
}

export function getCalloutContent({
  userId,
  userJoinedLeaderboard,
  isSales,
  selectedEstimators,
  allEstimators,
  leaderboardRankValues,
  type
}: {
  userId: string
  userJoinedLeaderboard: boolean
  isSales: boolean
  selectedEstimators: DashboardEstimator[]
  allEstimators: DashboardEstimator[]
  leaderboardRankValues: LeaderboardRankResponseItem
  type: 'winRate' | 'totalSold'
}): { title: string; description: string } {
  const { rank: currentRank, comment } = leaderboardRankValues ?? { rank: 0, comment: '' }
  const currentEstimator = selectedEstimators?.[0]
  const multiEstimatorsSelected = selectedEstimators?.length > 1
  const soloAdmin = allEstimators.length === 1
  const isViewingSelf = Boolean(
    isSales ||
      soloAdmin ||
      (currentEstimator && removeAuth0Prefix(userId) === removeAuth0Prefix(currentEstimator?.value))
  )
  const isWinRate = type === 'winRate'
  const wr10SentMin = isWinRate && comment === 'less than 10 quotes sent'
  const wrStartOfMonth = isWinRate && new Date().getDate() <= 7

  let title: string
  if (!userJoinedLeaderboard) {
    title = 'Uh oh!'
  } else if (wrStartOfMonth) {
    title = 'Hold tight!'
  } else if (multiEstimatorsSelected || !isViewingSelf) {
    title = 'Game on!'
  } else if (wr10SentMin) {
    title = 'Uh oh!'
  } else if (currentRank === 1) {
    title = 'Wow, leading the pack!'
  } else if (currentRank && currentRank <= 10) {
    title = 'Outstanding work!'
  } else if (currentRank && currentRank <= 100) {
    title = 'Crushing it!'
  } else if (currentRank && currentRank < 300) {
    title = 'Keep at it!'
  } else if (currentRank === 300) {
    title = "Don't drop off!"
  } else {
    title = 'Keep on climbing!'
  }

  let description: string
  if (!userJoinedLeaderboard) {
    description = `To access the ${isWinRate ? 'Win Rate' : 'Sales'} Leaderboard, click below.`
  } else if (wrStartOfMonth) {
    description = `Rankings are being calculated.`
  } else if (multiEstimatorsSelected || !isViewingSelf) {
    description = `Check out your team's performance and rankings.`
  } else if (wr10SentMin) {
    description = `You need to send 10 estimates before getting ranked this month.`
  } else if (currentRank && currentRank <= 300) {
    description = `You're ranked #${currentRank} out of the top 300 players this month.`
  } else {
    description = `You're not ranked in the top 300 players this month.`
  }

  return { title, description }
}
